import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { SharedStudyConfirmationPayload, SourceSubmission } from './types';

/* eslint-disable no-unused-vars */
type SharedStudyConfirmationModalProps = {
  confirmation: SharedStudyConfirmationPayload | null;
  loading: boolean;
  onCancel: () => void;
  onConfirm: (params: { applyToAll: boolean; selectedSourceAppIds: number[] }) => void;
};
/* eslint-enable no-unused-vars */

const getSubmissionLabel = (submission: SourceSubmission) =>
  `${submission.applno}-${submission.submission_type}-${submission.submission_number}`;

const getSubmissionDetails = (submission: SourceSubmission) => {
  const details = [
    submission.data_source ? `Source: ${submission.data_source.toUpperCase()}` : null,
    submission.document_ids?.length ? `Document IDs: ${submission.document_ids.join(', ')}` : null,
    submission.indication_ids?.length
      ? `Indication IDs: ${submission.indication_ids.join(', ')}`
      : null
  ].filter(Boolean);

  return details.join(' | ');
};

const renderSubmissionRow = (
  submission: SourceSubmission,
  selectedIds: number[],
  // eslint-disable-next-line no-unused-vars
  onToggle: (id: number) => void,
  loading: boolean
) => (
  <FormControlLabel
    key={submission.id}
    control={
      <Checkbox
        checked={selectedIds.includes(submission.id)}
        onChange={() => onToggle(submission.id)}
        disabled={loading}
      />
    }
    label={
      <Box>
        <Typography variant='body2' fontWeight={600}>
          {getSubmissionLabel(submission)}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {getSubmissionDetails(submission) || 'No source details available'}
        </Typography>
      </Box>
    }
  />
);

const SharedStudyConfirmationModal = ({
  confirmation,
  loading,
  onCancel,
  onConfirm
}: SharedStudyConfirmationModalProps) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (!confirmation) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(confirmation.all_source_submissions.map(submission => submission.id));
  }, [confirmation]);

  const groupedIds = useMemo(() => {
    if (!confirmation) {
      return new Set<number>();
    }

    return new Set([
      ...confirmation.previous_source_submissions.map(submission => submission.id),
      ...(confirmation.current_submission ? [confirmation.current_submission.id] : []),
      ...confirmation.next_source_submissions.map(submission => submission.id)
    ]);
  }, [confirmation]);

  const otherSourceSubmissions = useMemo(() => {
    if (!confirmation) {
      return [];
    }

    return confirmation.all_source_submissions.filter(submission => !groupedIds.has(submission.id));
  }, [confirmation, groupedIds]);

  if (!confirmation) {
    return null;
  }

  const toggleSubmission = (id: number) => {
    setSelectedIds(previous =>
      previous.includes(id) ? previous.filter(currentId => currentId !== id) : [...previous, id]
    );
  };

  const renderGroup = (title: string, submissions: SourceSubmission[]) => (
    <Box>
      <Typography variant='subtitle2' color='primary' gutterBottom>
        {title}
      </Typography>
      {submissions.length > 0 ? (
        <Stack spacing={0.5}>
          {submissions.map(submission =>
            renderSubmissionRow(submission, selectedIds, toggleSubmission, loading)
          )}
        </Stack>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          No submissions in this group.
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open onClose={loading ? undefined : onCancel} maxWidth='md' fullWidth>
      <DialogTitle>Shared Study Update</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant='body2'>
            {confirmation.message ||
              'This study is shared by other source submissions. Choose where to apply the edit.'}
          </Typography>

          {renderGroup('Previous Source Submissions', confirmation.previous_source_submissions)}
          <Divider />
          {renderGroup(
            'Current Source Submission',
            confirmation.current_submission ? [confirmation.current_submission] : []
          )}
          <Divider />
          {renderGroup('Next Source Submissions', confirmation.next_source_submissions)}
          {otherSourceSubmissions.length > 0 && (
            <>
              <Divider />
              {renderGroup('Other Source Submissions', otherSourceSubmissions)}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm({ applyToAll: false, selectedSourceAppIds: selectedIds })}
          disabled={loading || selectedIds.length === 0}>
          Apply Selected
        </Button>
        <Button
          variant='contained'
          onClick={() => onConfirm({ applyToAll: true, selectedSourceAppIds: [] })}
          disabled={loading}>
          {loading ? <CircularProgress size={18} color='inherit' /> : 'Apply to All'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharedStudyConfirmationModal;
